// Shared data layer for sign-outs.
//
// Active sign-outs live in a single top-level `signouts` collection — NOT under a
// class — so a sign-out is owned by the teacher who did it (teacherUID) and stamped
// with a room, and any school teacher can sign out any directory student. On sign-in
// the record is moved to the owning teacher's `users/{teacherUID}/signedInHistory`
// (which the reporting in SignOutHistory.js already reads) and deleted from `signouts`.
//
// Because docs are deleted on sign-in, `signouts` only ever holds ACTIVE records — so
// readers can query by a single equality field (teacherUID / schoolId / studentDoc)
// and need no time_in filter and no composite indexes.

import {
  collection, addDoc, doc, getDoc, setDoc, deleteDoc, query, where, getDocs,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";

export const SIGNOUTS = "signouts";

// Compose "Ms. Mack" (prefix + last name) from a user record; never the first name.
// Falls back to displayName. Mirrors the rule used on the Following board.
export function teacherDisplayName(data = {}) {
  // "Other" is the title dropdown's "no standard title" choice — don't render it literally.
  const title = data.title && data.title.toLowerCase() !== "other" ? data.title : "";
  return (
    [title, data.lastName].filter(Boolean).join(" ").trim() ||
    data.displayName ||
    "Unknown Teacher"
  );
}

// Create an active sign-out. `data` should carry: studentDocumentNumber, studentName,
// idNumber, grade, schoolId, teacherUID, teacherName, classId, className, room, location.
export function createSignOut(data) {
  return addDoc(collection(firestore, SIGNOUTS), { ...data, time_in: null });
}

// Move one active sign-out to the owning teacher's history, then delete it.
export async function signInOne(signoutId) {
  const ref = doc(firestore, SIGNOUTS, signoutId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const data = snap.data();
  if (data.teacherUID) {
    await setDoc(
      doc(firestore, "users", data.teacherUID, "signedInHistory", signoutId),
      { ...data, time_in: new Date().toISOString() }
    );
  }
  await deleteDoc(ref);
}

// Sign in a list of active sign-outs by id.
export async function signInMany(signoutIds) {
  for (const id of signoutIds) {
    await signInOne(id);
  }
}

// A student's currently-open sign-out, or null. `signouts` holds only active records,
// so a match by studentDocumentNumber is by definition active. schoolId scopes the
// query so the security rules can authorize it (a student can only be out once
// school-wide, and the sign-out carries the student's schoolId).
export async function findActiveSignOut(studentDocumentNumber, schoolId) {
  const constraints = [where("studentDocumentNumber", "==", studentDocumentNumber)];
  if (schoolId) constraints.unshift(where("schoolId", "==", schoolId));
  const snap = await getDocs(query(collection(firestore, SIGNOUTS), ...constraints));
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() };
}
